import type {
  ExportRequest,
  ExportResponse,
  Facets,
  LayerFeatureProps,
  ListCreate,
  ListDetail,
  ListSummary,
  Paginated,
  SiteDetail,
  SiteIdSet,
  SitePatch,
  SiteSummary,
  UnitDetail,
} from "@plenum/shared/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toParams } from "./search";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    // Cloudflare Access answers an unauthenticated /api call with a 302 to its login page on another
    // origin, which fetch cannot follow or read. Ask for the redirect itself instead and report it
    // as a 401 with its own code; SessionBanner sends the visitor to /login on that code.
    redirect: "manual",
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  if (res.type === "opaqueredirect") {
    throw new ApiError(401, "access_login", "Cloudflare Access login required");
  }
  if (res.status === 304) return undefined as T;
  if (!res.ok) {
    let body: { error?: { code?: string; message?: string; details?: unknown } } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      body = {};
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? "http_error",
      body.error?.message ?? `${res.status} ${res.statusText}`,
      body.error?.details,
    );
  }
  return (await res.json()) as T;
}

export type LayerFeature = {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: LayerFeatureProps;
};
export type LayerCollection = { type: "FeatureCollection"; features: LayerFeature[] };

export type OsmFeature = {
  type: "Feature";
  geometry:
    | { type: "Polygon"; coordinates: number[][][] }
    | { type: "MultiPolygon"; coordinates: number[][][][] };
  properties: { id: number; osm: string; n: string | null; c: number };
};
export type OsmCollection = { type: "FeatureCollection"; features: OsmFeature[] };

export type HealthResponse = {
  ok: boolean;
  version: string;
  layer_version: string | null;
  /** Cloudflare Access sign-out URL; null when the API runs without Access (local dev). */
  logout_url: string | null;
};

export type AppConfig = {
  band_default: { mw_min: number; mw_max: number };
  band_core: { mw_min: number; mw_max: number };
  presets: Array<{ id: string; label: string; rules: Record<string, unknown> }>;
  segments_default: string[];
  exclude_states_default: string[];
  prime_movers: { segment_a: string[]; segment_b: string[]; context: string[] };
  statuses: { default: string[]; mothballed: string[]; planned: string[]; retired: string[] };
  attribution: { map: string; data: string };
};

export const fetchHealth = () => api<HealthResponse>("/api/health");
export const fetchLayer = () => api<LayerCollection>("/api/layer");
export const fetchOsmLayer = () => api<OsmCollection>("/api/layer/osm");
export const fetchConfig = () => api<AppConfig>("/api/config");
export const fetchFacets = () => api<Facets>("/api/sites/facets");
export const fetchSiteIds = (filters: Record<string, unknown>) =>
  api<SiteIdSet>(`/api/sites/ids?${toParams(filters)}`);
export const fetchSites = (filters: Record<string, unknown>) =>
  api<
    Paginated<SiteSummary> & {
      summary: { n: number; units: number; mw: number; mwh: number | null };
    }
  >(`/api/sites?${toParams(filters)}`);
export const fetchSite = (id: number) => api<SiteDetail>(`/api/sites/${id}`);
export const fetchUnit = (id: number) => api<UnitDetail>(`/api/units/${id}`);
export const fetchLists = () => api<{ items: ListSummary[] }>("/api/lists");
export const fetchList = (id: number) => api<ListDetail>(`/api/lists/${id}`);

export const useHealth = () =>
  useQuery({ queryKey: ["health"], queryFn: fetchHealth, staleTime: 60_000 });
export const useConfig = () =>
  useQuery({ queryKey: ["config"], queryFn: fetchConfig, staleTime: Number.POSITIVE_INFINITY });
export const useFacets = () =>
  useQuery({ queryKey: ["facets"], queryFn: fetchFacets, staleTime: 5 * 60_000 });
export const useLayer = () =>
  useQuery({ queryKey: ["layer"], queryFn: fetchLayer, staleTime: Number.POSITIVE_INFINITY });
export const useOsmLayer = (enabled: boolean) =>
  useQuery({
    queryKey: ["layer", "osm"],
    queryFn: fetchOsmLayer,
    staleTime: Number.POSITIVE_INFINITY,
    enabled,
  });
export const useSiteIds = (filters: Record<string, unknown>) =>
  useQuery({
    queryKey: ["site-ids", filters],
    queryFn: () => fetchSiteIds(filters),
    placeholderData: (prev) => prev,
  });
export const useSites = (filters: Record<string, unknown>) =>
  useQuery({
    queryKey: ["sites", filters],
    queryFn: () => fetchSites(filters),
    placeholderData: (prev) => prev,
  });
export const useSite = (id: number | undefined) =>
  useQuery({
    queryKey: ["site", id],
    queryFn: () => fetchSite(id as number),
    enabled: id !== undefined,
  });
export const useUnit = (id: number | undefined) =>
  useQuery({
    queryKey: ["unit", id],
    queryFn: () => fetchUnit(id as number),
    enabled: id !== undefined,
  });
export const useLists = () => useQuery({ queryKey: ["lists"], queryFn: fetchLists });
export const useList = (id: number | undefined) =>
  useQuery({
    queryKey: ["list", id],
    queryFn: () => fetchList(id as number),
    enabled: id !== undefined,
  });

export function useCreateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ListCreate) =>
      api<ListSummary>("/api/lists", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lists"] });
      void qc.invalidateQueries({ queryKey: ["site"] });
    },
  });
}

export function useAddToList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, siteIds }: { listId: number; siteIds: number[] }) =>
      api<{ ok: boolean; added: number; item_count: number }>(`/api/lists/${listId}/items`, {
        method: "POST",
        body: JSON.stringify({ site_ids: siteIds }),
      }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["lists"] });
      void qc.invalidateQueries({ queryKey: ["list", v.listId] });
      void qc.invalidateQueries({ queryKey: ["site"] });
    },
  });
}

export function useRemoveFromList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, siteId }: { listId: number; siteId: number }) =>
      api<{ ok: boolean }>(`/api/lists/${listId}/items/${siteId}`, { method: "DELETE" }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["lists"] });
      void qc.invalidateQueries({ queryKey: ["list", v.listId] });
      void qc.invalidateQueries({ queryKey: ["site", v.siteId] });
    },
  });
}

export function useRenameList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, name }: { listId: number; name: string }) =>
      api<{ ok: boolean }>(`/api/lists/${listId}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["lists"] });
      void qc.invalidateQueries({ queryKey: ["list", v.listId] });
    },
  });
}

export function useDeleteList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listId: number) =>
      api<{ ok: boolean }>(`/api/lists/${listId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["lists"] });
      void qc.invalidateQueries({ queryKey: ["site"] });
    },
  });
}

export function usePatchSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, patch }: { siteId: number; patch: SitePatch }) =>
      api<{ ok: boolean; changed: string[] }>(`/api/sites/${siteId}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["site", v.siteId] });
      void qc.invalidateQueries({ queryKey: ["sites"] });
      if (v.patch.coordinates) {
        // The static layer is republished by the pipeline; move the pin locally until then.
        const { lat, lon } = v.patch.coordinates;
        qc.setQueryData<LayerCollection>(["layer"], (prev) =>
          prev
            ? {
                ...prev,
                features: prev.features.map((f) =>
                  f.properties.id === v.siteId
                    ? { ...f, geometry: { type: "Point", coordinates: [lon, lat] } }
                    : f,
                ),
              }
            : prev,
        );
      }
    },
  });
}

export function useExport() {
  return useMutation({
    mutationFn: (body: ExportRequest) =>
      api<ExportResponse>("/api/exports", { method: "POST", body: JSON.stringify(body) }),
  });
}

export const fetchStats = () => api<import("@plenum/shared/api").StatsResponse>("/api/stats");
export const fetchClimate = (id: number) =>
  api<import("@plenum/shared/api").ClimateResponse>(`/api/sites/${id}/climate`);
export const fetchSettings = () =>
  api<import("@plenum/shared/api").SettingsResponse>("/api/settings");
export const fetchIngest = () =>
  api<import("@plenum/shared/api").IngestResponse>("/api/admin/ingest");

export const useStats = () =>
  useQuery({ queryKey: ["stats"], queryFn: fetchStats, staleTime: 60_000 });
export const useClimate = (id: number | undefined) =>
  useQuery({
    queryKey: ["climate", id],
    queryFn: () => fetchClimate(id as number),
    enabled: id !== undefined,
    staleTime: 5 * 60_000,
  });
export const useSettings = () =>
  useQuery({ queryKey: ["settings"], queryFn: fetchSettings, staleTime: 60_000 });
export const useIngest = () =>
  useQuery({ queryKey: ["ingest"], queryFn: fetchIngest, staleTime: 30_000 });

export function usePatchSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import("@plenum/shared/api").SettingsPatch) =>
      api<import("@plenum/shared/api").SettingsResponse>("/api/settings", {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      qc.setQueryData(["settings"], data);
      void qc.invalidateQueries({ queryKey: ["config"] });
    },
  });
}

export type ReviewList = {
  items: import("@plenum/shared/api").ReviewItem[];
  counts: Record<string, number>;
  /** pending claims per field, whole queue */
  by_field?: Record<string, number>;
  /** sites with the most pending claims, whole queue */
  sites?: Array<{ id: number; name: string; n: number }>;
};
export const fetchReview = (params: Record<string, unknown>) =>
  api<ReviewList>(`/api/review?${toParams(params)}`);
export const useReview = (params: Record<string, unknown>) =>
  useQuery({ queryKey: ["review", params], queryFn: () => fetchReview(params), staleTime: 10_000 });

export function useReviewAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      claimId,
      action,
    }: {
      claimId: number;
      action: import("@plenum/shared/api").ReviewAction;
    }) =>
      api<{ ok: boolean; status: string; applied: boolean }>(`/api/review/${claimId}`, {
        method: "POST",
        body: JSON.stringify(action),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["review"] });
      void qc.invalidateQueries({ queryKey: ["site"] });
      void qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

export function useBatchAccept() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { min_conf?: number; site_id?: number }) =>
      api<{ ok: boolean; accepted: number }>("/api/review-batch/accept", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["review"] });
      void qc.invalidateQueries({ queryKey: ["site"] });
    },
  });
}

export const useCapture = (url: string | null) =>
  useQuery({
    queryKey: ["capture", url],
    queryFn: async () => {
      const res = await fetch(url as string);
      if (!res.ok) throw new ApiError(res.status, "capture_unavailable", "Capture unavailable");
      return res.text();
    },
    enabled: Boolean(url),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

export function useEnrich() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import("@plenum/shared/api").EnrichRequest) =>
      api<import("@plenum/shared/api").EnrichResponse>("/api/enrich", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ingest"] }),
  });
}

export const useApprovals = (enabled = true) =>
  useQuery({
    queryKey: ["approvals"],
    queryFn: () =>
      api<{ items: import("@plenum/shared/api").ApprovalRow[] }>("/api/approvals?status=pending"),
    enabled,
    staleTime: 30_000,
    retry: false,
  });

export function useDecideApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: "approve" | "reject" }) =>
      api<{ ok: boolean }>(`/api/approvals/${id}`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["approvals"] }),
  });
}

export const useOutreach = (params: { site_id?: number; list_id?: number }) =>
  useQuery({
    queryKey: ["outreach", params],
    queryFn: () =>
      api<{ items: import("@plenum/shared/api").OutreachEvent[] }>(
        `/api/outreach?${toParams(params)}`,
      ),
    staleTime: 15_000,
  });
export const useOutreachBoard = (listId: number | undefined) =>
  useQuery({
    queryKey: ["outreach-board", listId],
    queryFn: () => api<import("@plenum/shared/api").OutreachBoard>(`/api/outreach/board/${listId}`),
    enabled: listId !== undefined,
    staleTime: 15_000,
  });
export function useCreateOutreach() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import("@plenum/shared/api").OutreachCreate) =>
      api<{ ok: boolean; id: number }>("/api/outreach", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["outreach"] });
      void qc.invalidateQueries({ queryKey: ["outreach-board"] });
    },
  });
}
/** Outreach composer: ZeroGPT detector and humanizer behind /api/compose (Q72). */
export function useComposeCheck() {
  return useMutation({
    mutationFn: (body: import("@plenum/shared/api").ComposeCheck) =>
      api<import("@plenum/shared/api").ComposeCheckResponse>("/api/compose/check", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}
export function useComposeHumanize() {
  return useMutation({
    mutationFn: (body: import("@plenum/shared/api").ComposeHumanize) =>
      api<import("@plenum/shared/api").ComposeHumanizeResponse>("/api/compose/humanize", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}
export const useNotes = (entityType: string, entityId: number | undefined) =>
  useQuery({
    queryKey: ["notes", entityType, entityId],
    queryFn: () =>
      api<{ items: import("@plenum/shared/api").NoteRow[] }>(
        `/api/notes?${toParams({ entity_type: entityType, entity_id: entityId })}`,
      ),
    enabled: entityId !== undefined,
    staleTime: 15_000,
  });
export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: import("@plenum/shared/api").NoteCreate) =>
      api<{ ok: boolean; id: number }>("/api/notes", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (_d, v) =>
      void qc.invalidateQueries({ queryKey: ["notes", v.entity_type, v.entity_id] }),
  });
}
export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api<{ ok: boolean }>(`/api/notes/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notes"] }),
  });
}
export function usePatchContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      contactId,
      patch,
    }: {
      contactId: number;
      patch: import("@plenum/shared/api").ContactPatch;
    }) =>
      api<{ ok: boolean }>(`/api/contacts/${contactId}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["site"] }),
  });
}

// Explore pages: chart datasets published by the pipeline (ADR-008). Parsed with Zod at the
// boundary so a malformed dataset fails loudly rather than rendering an empty chart.
export const fetchChartManifest = async () => {
  const { ChartManifest } = await import("@plenum/shared/api");
  const raw = await api<unknown>("/api/charts/manifest");
  const empty = raw as { version: string | null };
  if (empty && empty.version === null) {
    return { version: null, generated_at: null, ingest_run_id: null, charts: [] } as const;
  }
  return ChartManifest.parse(raw);
};
export const fetchChart = async (slug: string) => {
  const { ChartDataset } = await import("@plenum/shared/api");
  return ChartDataset.parse(await api<unknown>(`/api/charts/${encodeURIComponent(slug)}`));
};
export const useChartManifest = () =>
  useQuery({
    queryKey: ["charts", "manifest"],
    queryFn: fetchChartManifest,
    staleTime: 5 * 60_000,
  });
export const useChart = (slug: string | undefined) =>
  useQuery({
    queryKey: ["charts", slug],
    queryFn: () => fetchChart(slug as string),
    enabled: slug !== undefined,
    staleTime: 5 * 60_000,
  });

export type Me = {
  email: string;
  role: "admin" | "editor" | "viewer";
  org: { id: number; slug: string | null; name: string | null };
};
/** The signed-in user; undefined while loading, null when there is no session. */
export const useMe = () =>
  useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api<Me>("/api/me");
      } catch (e) {
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return null;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
  });
